import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import LogoLoader from "@/components/LogoLoader";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const OwnerDashboard = lazy(() => import("@/pages/OwnerDashboard"));
const EmployeeDashboard = lazy(() => import("@/pages/EmployeeDashboard"));

function FullLoader() {
  return <LogoLoader />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Dashboard() {
  const { user } = useAuth();
  return user?.role === "owner" ? <OwnerDashboard /> : <EmployeeDashboard />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<FullLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<FullLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/dashboard"
          element={
            <Protected>
              <Dashboard />
            </Protected>
          }
        />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
